import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Modal from "./Modal";

interface FailReasonModalProps {
  visible: boolean;
  onClose: () => void;
  onSubmit: (failReason: string) => void;
  loading?: boolean;
  /**
   * Heading. Defaults to the sponsor-task wording; document and membership
   * rejection pass their own so the sheet never says "Reject Task" for a
   * document.
   */
  title?: string;
}

/**
 * Bottom-sheet modal asking for a rejection reason. Used for verification
 * document rejection, membership request rejection and sponsor task rejection.
 */
const FailReasonModal: React.FC<FailReasonModalProps> = ({
  visible,
  onClose,
  onSubmit,
  loading,
  title,
}) => {
  const { t } = useTranslation();
  const [failReason, setFailReason] = useState("");

  useEffect(() => {
    if (!visible) {
      setFailReason("");
    }
  }, [visible]);

  // Deliberately not cleared here. The parent owns `visible` and only closes
  // the sheet once the API call succeeded, so the `!visible` effect above is
  // what resets the field. Clearing eagerly threw away the reason the admin had
  // typed whenever the request failed.
  const handleSubmit = () => {
    if (failReason.trim()) {
      onSubmit(failReason.trim());
    }
  };

  const handleClose = () => {
    setFailReason("");
    onClose();
  };

  return (
    <Modal visible={visible} onClose={handleClose} position="bottom">
      <div className="flex flex-col items-center rounded-t-[20px] bg-white p-6 shadow-lg">
        <div className="mb-4 h-1 w-10 rounded bg-[#ddd]" />
        <div className="mb-2 text-center text-xl font-bold text-[#ffa217]">
          {title ?? t("Reject Task")}
        </div>
        <div className="mb-5 text-center text-sm leading-5 text-[#555]">
          {t("Please provide a reason for rejection:")}
        </div>
        <textarea
          className="mb-5 min-h-[100px] w-full max-h-[150px] resize-none rounded-xl border-[1.5px] border-[#e0e0e0] bg-[#fafafa] p-3.5 text-[15px] text-[#333]"
          placeholder={t("Enter reason...")}
          value={failReason}
          onChange={(e) => setFailReason(e.target.value)}
          disabled={loading}
          maxLength={200}
        />
        <div className="flex w-full gap-3">
          <button
            type="button"
            className="flex-1 rounded-xl bg-[#f0f0f0] py-3.5 text-[15px] font-semibold text-[#666] transition hover:bg-[#e5e5e5]"
            onClick={handleClose}
            disabled={loading}
          >
            {t("Cancel")}
          </button>
          <button
            type="button"
            className={`flex-1 rounded-xl bg-[#ffa217] py-3.5 text-[15px] font-bold text-white transition hover:opacity-90 ${
              loading || !failReason.trim() ? "opacity-50" : ""
            }`}
            onClick={handleSubmit}
            disabled={loading || !failReason.trim()}
          >
            {loading ? t("Submitting...") : t("Submit")}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default FailReasonModal;